// **React Imports
import {
  Fragment,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useLocation } from 'react-router-dom'

//**Component Imports
import OverlayLoading from './components/overlay-loading'
import RouterComponent from './router/router'

// **Third party Imports
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './axios/global'

// **Utils Imports
import { DOMAIN } from './utils/constant'
import { CHECK_CONTEST_URI_REGEX } from './utils/regex'
import {
  checkCandidateAccess,
  checkDeveloperAccess,
  deleteCookie,
  getCookie,
  getJWTToken,
  setRememberMeFlag
} from './utils/utils'

// ** Error Boundary Component
import ErrorBoundary from './pages/error'

// **Redux Imports
import { useDispatch } from 'react-redux'
import {
  setCurrentlyActivePlan,
  setSubscriptions,
  setUserInformation
} from './store/slice/auth'

// **API Imports
import { getConfig } from './axios/api/configurations'
import { getAllUserSubscriptions } from './axios/api/subscription'

// **Context Imports
import { SettingsContext } from './context/setting'

// **SCSS Imports
import './App.scss'

// **Favicon Imports
import DefaultFavicon from './assets/images/favicon.svg'

function App() {
  // hooks
  const dispatch = useDispatch()
  const { saveSettings } = useContext(SettingsContext)
  const location = useLocation()

  // Variables
  const match = useMemo(() => {
    if (location.pathname === '/c/profile') {
      return null
    }
    return location.pathname.match(CHECK_CONTEST_URI_REGEX)
  }, [location.pathname])

  const contestUri = useMemo(() => {
    return match ? match[1] : null
  }, [match])

  // state
  const [loading, setLoading] = useState({ subscription: true, config: true })
  const { settings } = useContext(SettingsContext)

  // API to get subscription
  const fetchUserSubscription = useCallback(async () => {
    // accessing token
    const accessToken = getJWTToken()

    if (!accessToken) {
      setLoading((prev) => ({ ...prev, subscription: false }))
      return
    }

    const response = await getAllUserSubscriptions()

    if (response?.isError) {
      dispatch(setCurrentlyActivePlan(null))
      dispatch(setSubscriptions(null))
    } else {
      const activePlan = response?.filter(
        (plan) => plan?.status?.toLowerCase() === 'active'
      )
      dispatch(setSubscriptions(response))
      dispatch(setCurrentlyActivePlan(activePlan?.[0] || null))
    }

    setLoading((prev) => ({ ...prev, subscription: false }))
  }, [dispatch])

  // API call to get config
  const fetchTenantConfig = useCallback(
    async (contestUri = null) => {
      const response = await getConfig(contestUri)

      if (response?.isError) {
        saveSettings({ tenantConfig: null })
      } else {
        saveSettings({ tenantConfig: response })
        if (response?.primaryColor) {
          sessionStorage.setItem('themeColor', response.primaryColor)
        }
      }
      setLoading((prev) => ({ ...prev, config: false }))
    },
    [saveSettings]
  )

  // Effect to set the cookie on tenant change
  useEffect(() => {
    const token = getCookie('user-token')
    const rememberMeFlag = getCookie('remember-me-flag') || false

    if (token) {
      // setting to local storage
      setRememberMeFlag(JSON.parse(rememberMeFlag))
      dispatch(setUserInformation({ jwt: token }))

      // clearing cross-subdomain cookies
      deleteCookie('user-token', `.${DOMAIN}`)
      deleteCookie('remember-me-flag', `.${DOMAIN}`)
    }
  }, [dispatch])

  // effect to fetch subscriptions
  useEffect(() => {
    if (!checkCandidateAccess() && !checkDeveloperAccess()) {
      fetchUserSubscription()
    } else {
      setLoading((prev) => ({ ...prev, subscription: false }))
    }
  }, [fetchUserSubscription])

  useEffect(() => {
    if (!checkDeveloperAccess()) {
      fetchTenantConfig(contestUri)
    } else {
      setLoading((prev) => ({ ...prev, config: false }))
    }
  }, [contestUri, fetchTenantConfig])

  useEffect(() => {
    const favicon = document.getElementById('favicon')
    if (favicon) {
      const newFavicon = settings?.tenantConfig?.favicon || DefaultFavicon
      favicon.href = newFavicon
    }
  }, [settings?.tenantConfig?.favicon])

  return (
    <Fragment>
      <ErrorBoundary>
        {!loading.config && !loading.subscription && (
          <Suspense fallback={<OverlayLoading />}>
            <RouterComponent />
          </Suspense>
        )}
      </ErrorBoundary>
      <ToastContainer
        closeOnClick
        autoClose={3000}
        position="top-right"
        hideProgressBar={true}
      />
    </Fragment>
  )
}

export default App
